import { CoverageOption, Driver, MonthlyReportDataUnit, Tractor, Trailer } from "@deathstar/types/northstar";
import { DriverRow, OOProgramPolicyStatus } from "@deathstar/types/waypoint";
import { Popover, useSnackbar } from "@deathstar/ui";
import {
    ArrowRightCircleIcon,
    BackspaceIcon,
    BuildingOfficeIcon,
    CheckCircleIcon,
    CheckIcon,
    EllipsisVerticalIcon,
    MinusCircleIcon,
    PencilSquareIcon,
    PlusCircleIcon,
    ShieldCheckIcon,
    TruckIcon,
} from "@heroicons/react/24/outline";
import { Tooltip } from "@material-ui/core";
import { AccessorColumnDef, CellContext, DisplayColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";
import { AccountIdProvider } from "../../api/useAccountId";
import { stringifyAddress } from "../certificates/certificatesUtil";
import equipmentColumns from "../equipment/EquipmentColumns";
import {
    getDriverChangeDetail,
    getPendingDriverChanges,
    getPendingPolicyAdditionChange,
    isPendingPolicyAdditionRow,
    PendingPolicyChangeIndicator,
    usePendingPolicyChanges,
} from "../pending-policy-changes/PendingPolicyChanges";
import PolicyViewDialog from "../policies/policy-view/PolicyViewDialog";
import { NewDriverDialog } from "./NewDriverDialog";
import OOProgramInviteForm from "./OOProgramInviteForm";
import RemoveDriverDialog from "./RemoveDriverDialog";

export interface DriverTableMeta {
    openTractorLinker(driverId: number): void;
    openTrailerLinker(driverId: number): void;
    openEmployerLinker(driverId: number): void;
    companyName?: string;
    /** Drivers with a pending removal can't be changed until the pending changes are completed */
    isPendingRemoval?(row: { accountId: string; id: number }): boolean;
}

const mobileCell: DisplayColumnDef<DriverRow> = {
    id: "mobile-only",
    cell: function MobileDriverCell(info) {
        const meta = info.table.options.meta as DriverTableMeta;
        const row = info.row.original;

        const { data: allTractors } = api.equipment.useFindTractors(row.accountId);
        const { data: allTrailers } = api.equipment.useFindTrailers(row.accountId);

        const [changeDialogOpen, setChangeDialogOpen] = useState(false);
        const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

        const tractors = row.tractors?.map((t1) => allTractors?.find((t2) => t1.id === t2.id)).filter(Boolean) as Tractor[];
        const trailers = row.trailers?.map((t1) => allTrailers?.find((t2) => t1.id === t2.id)).filter(Boolean) as Trailer[];
        const isPendingRemoval = meta?.isPendingRemoval?.(row) ?? false;

        if (isPendingPolicyAdditionRow(row)) {
            return (
                <div className="w-full space-y-1 text-left text-stone-600">
                    <p className="line-clamp-1 font-medium text-stone-800">{row.name}</p>
                    <p className="tabular-nums">{row.license || "[No license provided]"}</p>
                    <p className="text-sm">Hired {row.policyDoh ? moment(row.policyDoh).format("M/D/YY") : "[Unknown]"}</p>
                </div>
            );
        }

        return (
            <>
                <Popover>
                    <Popover.Content>
                        <Popover.Item
                            onClick={() => {
                                setChangeDialogOpen(true);
                            }}
                            disabled={isPendingRemoval || !!info.row.original.pendingChanges?.length}
                        >
                            <PencilSquareIcon className="h-5 w-5" />
                            <span>Update{info.row.original.pendingChanges?.length ? " (pending)" : ""}</span>
                        </Popover.Item>
                        <Popover.Item
                            onClick={() => {
                                meta.openTractorLinker(info.row.original.id);
                            }}
                            disabled={isPendingRemoval}
                        >
                            <TruckIcon className="h-5 w-5" />
                            <span>Change tractor</span>
                        </Popover.Item>
                        <Popover.Item
                            onClick={() => {
                                meta.openTrailerLinker(info.row.original.id);
                            }}
                            disabled={isPendingRemoval}
                        >
                            <TruckIcon className="h-5 w-5" />
                            <span>Change trailer</span>
                        </Popover.Item>
                        <Popover.Item
                            onClick={() => {
                                meta.openEmployerLinker(info.row.original.id);
                            }}
                            disabled={isPendingRemoval}
                        >
                            <BuildingOfficeIcon className="h-5 w-5" />
                            <span>Change employer</span>
                        </Popover.Item>
                    </Popover.Content>
                    <Popover.Button className="w-full space-y-1 text-left text-stone-600">
                        <div className="flex justify-between gap-2">
                            <p className="line-clamp-1 font-medium text-stone-800">{row.name}</p>
                            {row.pendingChanges?.length ? (
                                <p className="whitespace-nowrap rounded-full bg-yellow-50 px-2 text-yellow-700">Pending changes</p>
                            ) : null}
                        </div>
                        <div className="flex gap-4">
                            <p className="tabular-nums">{row.license || "[No license provided]"}</p>
                            <span>•</span>
                            <p className="text-sm">Hired {row.policyDoh ? moment(row.policyDoh).format("M/D/YY") : "[Unknown]"}</p>
                        </div>
                        {tractors?.length ? (
                            <div className="">Drives tractor #{tractors[0]!.unitNumber || `...${tractors[0]!.vin?.slice(-4) || ""}`}</div>
                        ) : null}
                        {trailers?.length ? (
                            <div className="">Pulls trailer #{trailers[0]!.unitNumber || `...${trailers[0]!.vin?.slice(-4) || ""}`}</div>
                        ) : null}
                        <div className="flex gap-4">
                            <p className="text-sm tabular-nums">{row.policyExp} years of experience</p>
                            {row.policyOwnerOperator && (
                                <>
                                    <span>•</span>
                                    <p className="text-sm">Independent Contractor</p>
                                </>
                            )}
                        </div>
                        <div className="flex gap-2">
                            {row.coverages?.length ? (
                                <span className="text-stone-600">
                                    {row.coverages
                                        .filter(
                                            (c) =>
                                                !c.isUpcoming &&
                                                c.coverageOptionId !== CoverageOption.Id.INDEPENDENT_CONTRACTOR_POLICY_MONITORING
                                        )
                                        .map((c) => CoverageOption.getMetadata(c.coverageOptionId).name)
                                        .sort()
                                        .join(", ")}
                                </span>
                            ) : (
                                <span className="text-stone-500">No coverages</span>
                            )}
                        </div>
                        {row.policyOwnerOperator ? (
                            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-stone-500">Employed by {meta.companyName}</p>
                        ) : row.employer ? (
                            <Tooltip
                                classes={{ tooltip: "bg-white shadow-lg rounded p-2 text-stone-800 font-normal border" }}
                                title={
                                    <div>
                                        <p>{row.employer.name}</p>
                                        <p>{stringifyAddress(row.employer)}</p>
                                        <p>{row.employer.email}</p>
                                        <p>{row.employer.fax}</p>
                                    </div>
                                }
                            >
                                <p className="overflow-hidden text-ellipsis whitespace-nowrap text-stone-500">
                                    Employed by {row.employer.name}
                                </p>
                            </Tooltip>
                        ) : (
                            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-yellow-600">Employer not set</p>
                        )}
                    </Popover.Button>
                </Popover>
                <NewDriverDialog
                    open={changeDialogOpen}
                    onClose={() => setChangeDialogOpen(false)}
                    defaultValues={{
                        atDate: moment().format("YYYY-MM-DD"),
                        coverages: info.row.original.coverages,
                        dob: info.row.original.dob || "",
                        doh: info.row.original.policyDoh || "",
                        exp: info.row.original.policyExp || 0,
                        license: info.row.original.license || "",
                        name: info.row.original.name || "",
                        state: info.row.original.state || "",
                        ownerOperator: !!info.row.original.policyOwnerOperator,
                        employer: info.row.original.employerId ?? undefined,
                        beneficiaries: info.row.original.beneficiaries ?? undefined,
                    }}
                    editingId={info.row.original.id}
                    deferPolicyChanges
                    pendingChangeDetail={getDriverChangeDetail(info.row.original.name)}
                />
                <RemoveDriverDialog
                    open={removeDialogOpen}
                    onClose={() => {
                        setRemoveDialogOpen(false);
                    }}
                    accountId={info.row.original.accountId}
                    unitId={info.row.original.id}
                    coverages={info.row.original.coverages}
                    defaultDate={moment().format("YYYY-MM-DD")}
                    deferPolicyChanges
                    pendingChangeDetail={getDriverChangeDetail(info.row.original.name)}
                />
            </>
        );
    },
    meta: { responsive: true },
};

const browserPending: DisplayColumnDef<DriverRow> = {
    id: "browserPending",
    header: () => null,
    cell: function BrowserPendingCell(info) {
        const { changes } = usePendingPolicyChanges();
        const row = info.row.original;
        const addition = getPendingPolicyAdditionChange(changes, row);
        const rowChanges = addition ? [addition] : getPendingDriverChanges(changes, row.accountId, row.id);

        return <PendingPolicyChangeIndicator changes={rowChanges} appearance={addition ? "addition" : "default"} />;
    },
    enableGlobalFilter: false,
    enableSorting: false,
    meta: { classes: { cell: "w-8" } },
};

export const nameColumn: AccessorColumnDef<DriverRow> = {
    accessorFn: (row) => row.name,
    cell: (info) => (
        <Tooltip
            classes={{ tooltip: "font-normal" }}
            title={
                <div>
                    <p>Date of birth: {info.row.original.dob ? moment(info.row.original.dob).format("MM/DD/YYYY") : "-"}</p>
                    <p>Experience: {info.row.original.policyExp}</p>
                </div>
            }
        >
            <div>
                <p className="font-medium">{info.row.original.name}</p>
                <p className="text-stone-600">{info.row.original.license}</p>
            </div>
        </Tooltip>
    ),
    header: "Name",
};

export const licenseColumn: AccessorColumnDef<DriverRow> = {
    accessorFn: (row) => row.license,
    header: "License",
    meta: {
        hidden: true,
    },
};

export const dobColumn: AccessorColumnDef<DriverRow> = {
    accessorFn: (row) => row.dob,
    id: "dob",
    header: () => <p className="text-right">DOB</p>,
    cell: (info) => <span>{info.getValue() ? moment.utc(info.getValue() as string).format("MM/DD/YYYY") : null}</span>,
    enableGlobalFilter: false,
    meta: {
        align: "right",
        classes: { cell: "tabular-nums" },
        hidden: true,
    },
    sortingFn: "datetime",
};

export const dohColumn: AccessorColumnDef<DriverRow> = {
    accessorFn: (row) => row.policyDoh,
    id: "doh",
    header: () => <p className="text-right">Date hired</p>,
    cell: (info) => <span>{info.getValue() ? moment.utc(info.getValue() as string).format("MM/DD/YYYY") : null}</span>,
    enableGlobalFilter: false,
    meta: {
        align: "right",
        classes: { cell: "tabular-nums" },
    },
    sortingFn: "datetime",
};

export const expColumn: AccessorColumnDef<DriverRow> = {
    id: "exp",
    accessorFn: (row) => row.policyExp,
    header: () => <p className="text-right">Experience (yrs)</p>,
    meta: {
        align: "right",
        classes: { cell: "tabular-nums" },
        hidden: true,
    },
    enableGlobalFilter: false,
    sortingFn: "alphanumeric",
};

export const actionsColumn: DisplayColumnDef<DriverRow> = {
    id: "actions",
    cell: function ActionsCell(info: CellContext<DriverRow, void>) {
        const navigate = useNavigate();
        const { data: ooProgram, refetch } = api.ooProgram.useGet(info.row.original.accountId);
        const [inviteFormOpen, setInviteFormOpen] = useState(false);
        const [decPageOpen, setDecPageOpen] = useState(false);
        const [changeDialogOpen, setChangeDialogOpen] = useState(false);
        const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

        const invite = api.ooProgram.useInvite({
            onSuccess: () => {
                setInviteFormOpen(false);
                refetch();
            },
        });

        const removeFromProgram = api.ooProgram.useRemove(info.row.original.accountId, {
            onSuccess: () => {
                useSnackbar.add("Removal request sent", { variant: "success", autoHideDuration: 7500, persist: true });
            },
        });
        const meta = info.table.options.meta as DriverTableMeta | undefined;

        if (isPendingPolicyAdditionRow(info.row.original) || meta?.isPendingRemoval?.(info.row.original)) {
            return null;
        }

        return (
            <>
                <Popover>
                    <Popover.Content>
                        {info.row.original.policyOwnerOperator ? (
                            info.row.original.ownerOperatorMembership ? (
                                <>
                                    <Popover.Item
                                        onClick={() => {
                                            setDecPageOpen(true);
                                        }}
                                    >
                                        <ShieldCheckIcon className="h-5 w-5" />
                                        <span>View dec page</span>
                                    </Popover.Item>
                                    <Popover.Item
                                        onClick={() => {
                                            navigate(`/oo/${info.row.original.ownerOperatorMembership!.id}/policies/active`);
                                        }}
                                    >
                                        <ArrowRightCircleIcon className="h-5 w-5" />
                                        <span>View ind. contractor</span>
                                    </Popover.Item>
                                    <Popover.Item
                                        onClick={() => {
                                            removeFromProgram.mutate(info.row.original.ownerOperatorMembership!.id);
                                        }}
                                    >
                                        <MinusCircleIcon className="h-5 w-5" />
                                        Request to remove from program
                                    </Popover.Item>
                                    <hr className="my-1" />
                                </>
                            ) : ooProgram?.invitations.some((i) => i.license === info.row.original.license) ? (
                                <Popover.Item disabled>
                                    <CheckCircleIcon className="h-5 w-5" />
                                    <span>Invited to Navigator IC program</span>
                                </Popover.Item>
                            ) : (
                                <Popover.Item onClick={() => setInviteFormOpen(true)}>
                                    <PlusCircleIcon className="h-5 w-5" />
                                    <span>Invite to Navigator IC program</span>
                                </Popover.Item>
                            )
                        ) : null}
                        <Popover.Item
                            onClick={() => {
                                setChangeDialogOpen(true);
                            }}
                            disabled={!!info.row.original.pendingChanges?.length}
                        >
                            <PencilSquareIcon className="h-5 w-5" />
                            <span>Update{info.row.original.pendingChanges?.length ? " (pending)" : ""}</span>
                        </Popover.Item>
                        <Popover.Item
                            onClick={() => {
                                setRemoveDialogOpen(true);
                            }}
                            disabled={!!info.row.original.pendingChanges?.length}
                        >
                            <BackspaceIcon className="h-5 w-5" />
                            <span>Remove</span>
                        </Popover.Item>
                    </Popover.Content>
                    <Popover.Button className="rounded p-1 hover:bg-stone-100">
                        <EllipsisVerticalIcon className="h-5 w-5" />
                    </Popover.Button>
                </Popover>
                <NewDriverDialog
                    open={changeDialogOpen}
                    onClose={() => setChangeDialogOpen(false)}
                    defaultValues={{
                        atDate: moment().format("YYYY-MM-DD"),
                        coverages: info.row.original.coverages,
                        dob: info.row.original.dob || "",
                        doh: info.row.original.policyDoh || "",
                        exp: info.row.original.policyExp || 0,
                        license: info.row.original.license || "",
                        name: info.row.original.name || "",
                        state: info.row.original.state || "",
                        ownerOperator: !!info.row.original.policyOwnerOperator,
                        employer: info.row.original.employerId ?? undefined,
                        beneficiaries: info.row.original.beneficiaries ?? undefined,
                    }}
                    editingId={info.row.original.id}
                    deferPolicyChanges
                    pendingChangeDetail={getDriverChangeDetail(info.row.original.name)}
                />
                {info.row.original.ownerOperatorMembership && (
                    <AccountIdProvider accountId={info.row.original.ownerOperatorMembership.id}>
                        <PolicyViewDialog
                            policyId={info.row.original.ownerOperatorMembership.policies[0]?.id}
                            open={decPageOpen}
                            onClose={() => setDecPageOpen(false)}
                        />
                    </AccountIdProvider>
                )}
                <OOProgramInviteForm
                    open={inviteFormOpen}
                    onClose={() => setInviteFormOpen(false)}
                    onSubmit={(data) => {
                        invite.mutate({
                            accountId: info.row.original.accountId,
                            ...data,
                        });
                    }}
                    defaultValues={{
                        name: info.row.original.name || "",
                        license: info.row.original.license || "",
                    }}
                />
                <RemoveDriverDialog
                    open={removeDialogOpen}
                    onClose={() => {
                        setRemoveDialogOpen(false);
                    }}
                    accountId={info.row.original.accountId}
                    unitId={info.row.original.id}
                    coverages={info.row.original.coverages}
                    defaultDate={moment().format("YYYY-MM-DD")}
                    deferPolicyChanges
                    pendingChangeDetail={getDriverChangeDetail(info.row.original.name)}
                />
            </>
        );
    },
};

export const ownerOperatorColumn: AccessorColumnDef<DriverRow> = {
    id: "owner-operator",
    accessorFn: (row) => row.policyOwnerOperator,
    header: "IC",
    cell: function OwnerOperatorCell(info) {
        const { data: ooProgram } = api.ooProgram.useGet(info.row.original.accountId);

        if (!info.row.original.policyOwnerOperator) {
            return null;
        }

        if (info.row.original.ownerOperatorMembership) {
            return (
                <div className="flex w-max items-center gap-2 rounded-full bg-emerald-50 px-2 text-xs text-emerald-600 small-caps">
                    <CheckIcon className="h-3 w-3" />
                    <span>joined</span>
                </div>
            );
        }

        if (ooProgram?.invitations.some((i) => i.license === info.row.original.license)) {
            return (
                <div className="flex w-max items-center gap-2 rounded-full bg-yellow-50 px-2 text-xs text-yellow-600 small-caps">
                    <CheckIcon className="h-3 w-3" />
                    <span>invited</span>
                </div>
            );
        }

        return <CheckIcon className="mx-2 h-3 w-3" />;
    },
};

export const coveragesColumn: AccessorColumnDef<DriverRow> = {
    id: "coverages",
    accessorFn: (row) =>
        row.coverages
            ?.filter((c) => c.coverageOptionId !== CoverageOption.Id.INDEPENDENT_CONTRACTOR_POLICY_MONITORING)
            .sort((a, b) =>
                CoverageOption.getMetadata(a.coverageOptionId).defaultAbbreviation.localeCompare(
                    CoverageOption.getMetadata(b.coverageOptionId).defaultAbbreviation
                )
            )
            .map((c) => CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation)
            .join(", "),
    cell: (info) => {
        const coverages = info.row.original.coverages?.filter((c) => !c.isUpcoming);
        const ooCoverages = info.row.original.ownerOperatorMembership?.coverages.sort((a, b) =>
            CoverageOption.getMetadata(a).defaultAbbreviation.localeCompare(CoverageOption.getMetadata(b).defaultAbbreviation)
        );
        if (coverages?.length || ooCoverages?.length) {
            const sortedCoverages = coverages?.length
                ? coverages
                      .filter((c) => !c.isUpcoming && c.coverageOptionId !== CoverageOption.Id.INDEPENDENT_CONTRACTOR_POLICY_MONITORING)
                      .sort((a, b) =>
                          CoverageOption.getMetadata(a.coverageOptionId).defaultAbbreviation.localeCompare(
                              CoverageOption.getMetadata(b.coverageOptionId).defaultAbbreviation
                          )
                      )
                : [];
            return (
                <Tooltip
                    classes={{ tooltip: "font-normal" }}
                    title={
                        sortedCoverages.length || ooCoverages?.length ? (
                            <ul>
                                {sortedCoverages.map((c) => (
                                    <li key={c.policyId + c.coverageOptionId}>
                                        {CoverageOption.getMetadata(c.coverageOptionId).name}:{" "}
                                        <span className="text-emerald-600">Active</span>
                                    </li>
                                ))}
                                {info.row.original.hasWorkComp && (
                                    <li>
                                        {CoverageOption.getMetadata(CoverageOption.Id.WORKERS_COMPENSATION).name}:{" "}
                                        <span className="text-emerald-600">Active</span>
                                    </li>
                                )}
                                {!!ooCoverages?.length && <li className="text-base text-stone-500 small-caps">IC Policy Monitoring:</li>}
                                {ooCoverages?.map((c) => (
                                    <li key={c} className="ml-4">
                                        {CoverageOption.getMetadata(c).name}:{" "}
                                        <span
                                            className={
                                                !info.row.original.ownerOperatorMembership?.status ||
                                                info.row.original.ownerOperatorMembership?.status === OOProgramPolicyStatus.Active
                                                    ? "text-emerald-600"
                                                    : info.row.original.ownerOperatorMembership?.status === OOProgramPolicyStatus.Inactive
                                                    ? "text-red-600"
                                                    : info.row.original.ownerOperatorMembership?.status ===
                                                      OOProgramPolicyStatus.PendingCancellation
                                                    ? "text-yellow-600"
                                                    : ""
                                            }
                                        >
                                            {info.row.original.ownerOperatorMembership?.status || "Active"}
                                        </span>
                                    </li>
                                ))}
                                {info.row.original.policyOwnerOperator &&
                                    !info.row.original.coverages?.some(
                                        (c) => !c.isUpcoming && c.coverageOptionId === CoverageOption.Id.NON_TRUCKING_LIABILITY
                                    ) &&
                                    !info.row.original.ownerOperatorMembership && (
                                        <>
                                            <li className="text-base text-stone-500 small-caps">IC Policy Monitoring:</li>
                                            <li className="ml-4">
                                                Non-Trucking Liability: <span className="text-stone-400">Unknown</span>
                                            </li>
                                        </>
                                    )}
                            </ul>
                        ) : (
                            ""
                        )
                    }
                >
                    <div className="flex w-max flex-wrap gap-1 border-b border-dashed border-stone-400">
                        {sortedCoverages.map((c) => (
                            <span key={c.policyId + c.coverageOptionId}>
                                {CoverageOption.getMetadata(c.coverageOptionId).defaultAbbreviation}
                            </span>
                        ))}
                        {info.row.original.hasWorkComp && (
                            <span>{CoverageOption.getMetadata(CoverageOption.Id.WORKERS_COMPENSATION).defaultAbbreviation}</span>
                        )}
                        {info.row.original.policyOwnerOperator ? (
                            info.row.original.ownerOperatorMembership ? (
                                ooCoverages?.length ? (
                                    (ooCoverages.map((c) => CoverageOption.getMetadata(c).defaultAbbreviation) || []).sort().map((abbr) => (
                                        <span
                                            key={abbr}
                                            className={
                                                info.row.original.ownerOperatorMembership!.status === OOProgramPolicyStatus.Inactive
                                                    ? "text-red-600"
                                                    : info.row.original.ownerOperatorMembership!.status ===
                                                      OOProgramPolicyStatus.PendingCancellation
                                                    ? "text-yellow-600"
                                                    : ""
                                            }
                                        >
                                            {abbr}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-red-600">NTL?</span>
                                )
                            ) : null
                        ) : null}
                    </div>
                </Tooltip>
            );
        } else {
            return <span className="font-medium">[Not covered]</span>;
        }
    },
    header: "Active coverages",
    enableGlobalFilter: false,
    sortingFn: "alphanumeric",
};

export const linkedTractorsColumn: AccessorColumnDef<DriverRow> = {
    id: "tractors",
    header: "Tractor(s)",
    accessorFn: (row) => row.tractors,
    cell: function LinkedTractorCell(info) {
        const { data: tractors } = api.equipment.useFindTractors(info.row.original.accountId);
        const meta = info.table.options.meta as DriverTableMeta;
        const linked = info.row.original.tractors?.map((t1) => tractors?.find((t2) => t1.id === t2.id)).filter(Boolean) as Tractor[];

        if (isPendingPolicyAdditionRow(info.row.original) || meta?.isPendingRemoval?.(info.row.original)) {
            return null;
        }

        const displayLimit = 4;
        const visibleTractors = linked?.slice(0, displayLimit) || [];
        const remainingCount = (linked?.length || 0) - displayLimit;

        return (
            <Tooltip
                classes={{ tooltip: "bg-white shadow-lg rounded p-3 text-stone-800 font-normal border max-w-2xl" }}
                title={
                    linked?.length ? (
                        <div className="max-h-96 overflow-y-auto">
                            <div className="space-y-2">
                                <div className="sticky top-0 border-b border-stone-200 bg-white pb-1 text-sm font-semibold">
                                    {linked.length} Tractor{linked.length > 1 ? "s" : ""}
                                </div>
                                {linked.map((unit, index) => (
                                    <div key={unit.id} className={index > 0 ? "border-t border-stone-100 pt-2" : ""}>
                                        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
                                            <span className="font-medium text-stone-500">Unit:</span>
                                            <span className="flex items-center justify-between">
                                                <span>#{unit.unitNumber}</span>
                                                {unit.policyOwnerOperator && (
                                                    <span className="text-sm text-blue-600 small-caps">IC owned</span>
                                                )}
                                            </span>
                                            {unit.make && (
                                                <>
                                                    <span className="font-medium text-stone-500">Make:</span>
                                                    <span>{unit.make}</span>
                                                </>
                                            )}
                                            {unit.type?.name && (
                                                <>
                                                    <span className="font-medium text-stone-500">Type:</span>
                                                    <span>{unit.type.name}</span>
                                                </>
                                            )}
                                            {unit.vin && (
                                                <>
                                                    <span className="font-medium text-stone-500">VIN:</span>
                                                    <span className="font-mono text-xs">{unit.vin}</span>
                                                </>
                                            )}
                                            <span className="font-medium text-stone-500">Value:</span>
                                            <span>
                                                {unit.policyAcv
                                                    ? "ACV"
                                                    : unit.policyValue?.toLocaleString("en-us", {
                                                          style: "currency",
                                                          currency: "usd",
                                                          maximumFractionDigits: 0,
                                                      }) || "-"}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        ""
                    )
                }
                interactive
                leaveDelay={200}
            >
                <button
                    className="whitespace-nowrap rounded px-2 py-1 text-left ring-blue-700 hover:bg-stone-50 focus:bg-stone-100 focus:ring-1"
                    onClick={() => {
                        meta.openTractorLinker(info.row.original.id);
                    }}
                >
                    {linked?.length ? (
                        <span className="flex items-center gap-1">
                            <span>
                                {visibleTractors
                                    .map((t) => (t!.unitNumber ? `#${t!.unitNumber}` : `#...${t!.vin?.slice(-4) || ""}`))
                                    .join(", ")}
                            </span>
                            {remainingCount > 0 && <span className="text-stone-500">+{remainingCount}</span>}
                        </span>
                    ) : (
                        <span className="rounded-lg border-2 border-dashed border-orange-400 px-2 py-0.5 italic text-stone-400">
                            Unassigned
                        </span>
                    )}
                </button>
            </Tooltip>
        );
    },
    enableSorting: false,
    enableGlobalFilter: false,
};

export const linkedTrailersColumn: AccessorColumnDef<DriverRow> = {
    id: "trailers",
    header: "Trailer(s)",
    accessorFn: (row) => row.trailers,
    cell: function LinkedTrailerCell(info) {
        const { data: trailers } = api.equipment.useFindTrailers(info.row.original.accountId);
        const meta = info.table.options.meta as DriverTableMeta;
        const linked = info.row.original.trailers?.map((t1) => trailers?.find((t2) => t1.id === t2.id)).filter(Boolean) as Trailer[];

        if (isPendingPolicyAdditionRow(info.row.original) || meta?.isPendingRemoval?.(info.row.original)) {
            return null;
        }

        const displayLimit = 4;
        const visibleTrailers = linked?.slice(0, displayLimit) || [];
        const remainingCount = (linked?.length || 0) - displayLimit;

        return (
            <Tooltip
                classes={{ tooltip: "bg-white shadow-lg rounded p-3 text-stone-800 font-normal border max-w-2xl" }}
                title={
                    linked?.length ? (
                        <div className="max-h-96 overflow-y-auto">
                            <div className="space-y-2">
                                <div className="sticky top-0 border-b border-stone-200 bg-white pb-1 text-sm font-semibold">
                                    {linked.length} Trailer{linked.length > 1 ? "s" : ""}
                                </div>
                                {linked.map((unit, index) => (
                                    <div key={unit.id} className={index > 0 ? "border-t border-stone-100 pt-2" : ""}>
                                        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
                                            <span className="font-medium text-stone-500">Unit:</span>
                                            <span className="flex items-center justify-between">
                                                <span>#{unit.unitNumber}</span>
                                                {unit.policyOwnerOperator && (
                                                    <span className="text-sm text-blue-600 small-caps">IC owned</span>
                                                )}
                                            </span>
                                            {unit.make && (
                                                <>
                                                    <span className="font-medium text-stone-500">Make:</span>
                                                    <span>{unit.make}</span>
                                                </>
                                            )}
                                            {unit.type?.name && (
                                                <>
                                                    <span className="font-medium text-stone-500">Type:</span>
                                                    <span>{unit.type.name}</span>
                                                </>
                                            )}
                                            {unit.vin && (
                                                <>
                                                    <span className="font-medium text-stone-500">VIN:</span>
                                                    <span className="font-mono text-xs">{unit.vin}</span>
                                                </>
                                            )}
                                            <span className="font-medium text-stone-500">Value:</span>
                                            <span>
                                                {unit.policyAcv
                                                    ? "ACV"
                                                    : unit.policyValue?.toLocaleString("en-us", {
                                                          style: "currency",
                                                          currency: "usd",
                                                          maximumFractionDigits: 0,
                                                      }) || "-"}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        ""
                    )
                }
                interactive
                leaveDelay={200}
            >
                <button
                    className="whitespace-nowrap rounded px-2 py-1 text-left ring-blue-700 hover:bg-stone-50 focus:bg-stone-100 focus:ring-1"
                    onClick={() => {
                        meta.openTrailerLinker(info.row.original.id);
                    }}
                >
                    {linked?.length ? (
                        <span className="flex items-center gap-1">
                            <span>
                                {visibleTrailers
                                    .map((t) => (t!.unitNumber ? `#${t!.unitNumber}` : `#...${t!.vin?.slice(-4) || ""}`))
                                    .join(", ")}
                            </span>
                            {remainingCount > 0 && <span className="text-stone-500">+{remainingCount}</span>}
                        </span>
                    ) : (
                        <span className="rounded-lg border-2 border-dashed border-orange-400 px-2 py-0.5 italic text-stone-400">
                            Unassigned
                        </span>
                    )}
                </button>
            </Tooltip>
        );
    },
    enableSorting: false,
    enableGlobalFilter: false,
};

export const employerColumn: AccessorColumnDef<DriverRow | MonthlyReportDataUnit<Driver>> = {
    id: "employer",
    header: "Employer",
    accessorFn: (row) => row.employer?.name || "",
    cell: function EmployerCell(info) {
        const meta = info.table.options.meta as DriverTableMeta;
        const employer = info.row.original.employer;

        if (isPendingPolicyAdditionRow(info.row.original) || meta?.isPendingRemoval?.(info.row.original)) {
            return null;
        }

        if (!info.row.original.policyOwnerOperator && (!("isOwnerOperator" in info.row.original) || !info.row.original.isOwnerOperator)) {
            return <div className="max-w-36 overflow-hidden text-ellipsis whitespace-nowrap px-2 text-stone-500">{meta.companyName}</div>;
        }

        return (
            <Tooltip
                classes={{ tooltip: "bg-white shadow-lg rounded p-2 text-stone-800 font-normal border" }}
                title={
                    employer ? (
                        <div>
                            <p>{employer.name}</p>
                            <p>{stringifyAddress(employer)}</p>
                            <p>{employer.email}</p>
                            <p>{employer.fax}</p>
                        </div>
                    ) : (
                        ""
                    )
                }
            >
                <button
                    className="max-w-36 overflow-hidden text-ellipsis whitespace-nowrap rounded px-2 py-1 text-left ring-blue-700 hover:bg-stone-50 focus:bg-stone-100 focus:ring-1"
                    onClick={() => {
                        meta.openEmployerLinker(info.row.original.id);
                    }}
                >
                    {employer ? (
                        <span>{employer.name}</span>
                    ) : (
                        <span className="rounded-lg border-2 border-dashed border-orange-400 px-2 py-0.5 italic text-stone-400">
                            Unassigned
                        </span>
                    )}
                </button>
            </Tooltip>
        );
    },
    enableSorting: true,
    enableGlobalFilter: true,
};

export const DriverColumns = {
    mobileCell,
    browserPending,
    nameColumn,
    pending: equipmentColumns.pending as AccessorColumnDef<DriverRow>,
    upcomingDate: equipmentColumns.upcomingDate as AccessorColumnDef<DriverRow>,
    licenseColumn,
    dobColumn,
    dohColumn,
    expColumn,
    ownerOperatorColumn,
    coveragesColumn,
    linkedTractorsColumn,
    linkedTrailersColumn,
    employerColumn,
    actionsColumn,
};
